import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  PLATFORM_ID,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Компактный «текст + кнопка копирования».
 *
 * Используется в `mat-list-item` (`<app-copy-text matListItemLine ...>`),
 * чтобы оператор в один клик скопировал ФИО / телефон / дату и т.п.
 *
 * - Сначала пробует нативный `navigator.clipboard.writeText()`.
 * - В фоллбэке — скрытая `<textarea>` + `document.execCommand('copy')`
 *   (нужно для не-secure контекстов: Telegram Mini App, старые WebView).
 * - В небраузерной среде (SSR / prerender) клик тихо игнорируется.
 */
@Component({
  selector: 'app-copy-text',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './copy-text.component.html',
  styleUrl: './copy-text.component.scss',
})
export class CopyTextComponent {
  @Input() value: string = '';
  @Input() label?: string;

  private readonly snackBar = inject(MatSnackBar);
  private readonly platformId = inject(PLATFORM_ID);

  onCopy(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const text = this.value ?? '';
    if (!text) {
      return;
    }

    const showSuccess = (): void => {
      this.snackBar.open('Скопировано', 'ОК', { duration: 1500 });
    };
    const showError = (): void => {
      this.snackBar.open('Не удалось скопировать', 'ОК', { duration: 2000 });
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(showSuccess, () => {
        if (this.fallbackCopy(text)) {
          showSuccess();
        } else {
          showError();
        }
      });
      return;
    }
    if (this.fallbackCopy(text)) {
      showSuccess();
    } else {
      showError();
    }
  }

  /**
   * Фоллбэк для не-secure контекстов: создаём скрытую `<textarea>`,
   * копируем через `document.execCommand('copy')` и убираем за собой.
   */
  private fallbackCopy(text: string): boolean {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}
